import { useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  FileUp,
  Loader2,
  Trash2,
} from "lucide-react";

import {
  deleteCustomSpranca,
  extractSpranca,
  saveCustomSpranca,
  ApiError,
  type SprancaDict,
  type SprancaExtractResponse,
  type SprancaFieldConfidence,
  type SprancaSectionNode,
} from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useOfficeStore } from "./useOfficeStore";
import {
  SPRANCA_REVIEW_THRESHOLD,
  lowConfidencePaths,
  updateHeaderVar,
  updateSectionAtPath,
} from "./sprancaReview";

/** Canonical var tokens the header-row select offers — mirror of
 * ``permitpilot/docgen/spranca/variables.CANONICAL_TOKENS`` (header set). */
const VAR_OPTIONS: { value: string; label: string }[] = [
  { value: "gradevina", label: "građevina" },
  { value: "lokacija_kc_ko", label: "lokacija (k.č., k.o.)" },
  { value: "faza", label: "faza izrade" },
  { value: "strukovna_odrednica", label: "strukovna odrednica" },
  { value: "mapa", label: "mapa" },
  { value: "zajednicka_oznaka", label: "zajednička oznaka projekta" },
  { value: "broj_projekta", label: "broj projekta" },
  { value: "investitor_full", label: "investitor" },
  { value: "izradivac_full", label: "izrađivač" },
  { value: "mjesto_datum", label: "mjesto i datum izrade" },
];

/**
 * D1 — upload the office's own špranca, review the extracted model with
 * amber/„Potvrdi" stamps (same visual contract as the extraction UI, but
 * local state — this is Postavke, not project data), and save explicitly.
 * Mentalni model korisnika: „špranca + generalije → gotova mapa".
 */
export function SprancaUploadPanel() {
  const customSpranca = useOfficeStore((s) => s.customSpranca);
  const setCustomSpranca = useOfficeStore((s) => s.setCustomSpranca);
  const setSprancaId = useOfficeStore((s) => s.setSprancaId);

  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<"extract" | "save" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<SprancaExtractResponse | null>(null);
  const [confirmed, setConfirmed] = useState<ReadonlySet<string>>(new Set());

  async function onPickFile(file: File | null) {
    if (!file) return;
    setBusy("extract");
    setError(null);
    try {
      const res = await extractSpranca(file);
      setReview(res);
      setConfirmed(new Set());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Učitavanje šprance nije uspjelo.");
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onSave() {
    if (!review) return;
    setBusy("save");
    setError(null);
    try {
      const saved = await saveCustomSpranca(review.spranca);
      setCustomSpranca(saved);
      setSprancaId("custom");
      setReview(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Spremanje šprance nije uspjelo.");
    } finally {
      setBusy(null);
    }
  }

  async function onRemoveSaved() {
    setError(null);
    try {
      await deleteCustomSpranca();
    } catch {
      // The localStorage mirror is the working copy — a failed server
      // delete must not keep the UI stuck with a removed špranca.
    }
    setCustomSpranca(null);
  }

  const patchSpranca = (fn: (s: SprancaDict) => SprancaDict) =>
    setReview((r) => (r ? { ...r, spranca: fn(r.spranca) } : r));

  const openCount = review
    ? lowConfidencePaths(review.confidence, confirmed).length
    : 0;

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={fileRef}
        type="file"
        accept=".docx,.docm,.pdf"
        className="hidden"
        onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
      />

      {customSpranca && !review && (
        <div className="flex items-center justify-between rounded-md border border-green-200 bg-green-50 px-3 py-2">
          <span className="text-[0.8rem] text-green-800">
            <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
            Učitana špranca: <strong>{customSpranca.name}</strong>
          </span>
          <button
            type="button"
            onClick={() => void onRemoveSaved()}
            className="inline-flex items-center gap-1 text-[0.72rem] text-stone-500 hover:text-red-700"
            title="Ukloni učitanu šprancu (izbor se vraća na Permitko standard)."
          >
            <Trash2 className="h-3 w-3" /> ukloni
          </button>
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy === "extract"}
        onClick={() => fileRef.current?.click()}
        className="self-start"
      >
        {busy === "extract" ? (
          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
        ) : (
          <FileUp className="mr-1 h-3.5 w-3.5" />
        )}
        {customSpranca ? "Učitaj drugu šprancu…" : "Učitaj svoju šprancu…"}
      </Button>
      <span className="text-[0.72rem] text-stone-400">
        .docx, .docm ili PDF ispisan iz Worda. Dokument se obrađuje i ne
        sprema — spremaš tek pregledani rezultat.
      </span>

      {error && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[0.78rem] text-red-700">
          {error}
        </p>
      )}

      {review && (
        <div className="mt-1 flex flex-col gap-3 rounded-md border border-stone-200 bg-stone-50 p-3">
          {review.warnings.length > 0 && (
            <ul className="flex list-disc flex-col gap-0.5 pl-4 text-[0.72rem] text-amber-700">
              {review.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}

          <label className="flex flex-col gap-1">
            <span className="flex items-center gap-2 text-[0.78rem] font-medium text-stone-600">
              Naziv šprance
              <Stamp
                path="name"
                confidence={review.confidence}
                confirmed={confirmed}
                onConfirm={confirm}
              />
            </span>
            <Input
              value={review.spranca.name}
              onChange={(e) => patchSpranca((s) => ({ ...s, name: e.target.value }))}
            />
          </label>

          {review.spranca.header.rows.length > 0 && (
            <div className="flex flex-col gap-1">
              <p className="text-[0.72rem] font-semibold uppercase tracking-wide text-stone-400">
                Zaglavlje — redovi tablice
              </p>
              {review.spranca.header.rows.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-44 truncate text-[0.78rem] text-stone-600" title={row.label}>
                    {row.label || "(bez oznake)"}
                  </span>
                  <select
                    className="h-8 rounded-md border border-stone-200 bg-white px-1 text-[0.78rem]"
                    value={row.var}
                    onChange={(e) =>
                      patchSpranca((s) => updateHeaderVar(s, i, e.target.value))
                    }
                  >
                    {VAR_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <Stamp
                    path={`header.rows.${i}.var`}
                    confidence={review.confidence}
                    confirmed={confirmed}
                    onConfirm={confirm}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <p className="text-[0.72rem] font-semibold uppercase tracking-wide text-stone-400">
              Sekcije dokumenta
            </p>
            <SectionTree
              nodes={review.spranca.sections}
              prefix=""
              confidence={review.confidence}
              confirmed={confirmed}
              onConfirm={confirm}
              onPatch={(path, patch) =>
                patchSpranca((s) => ({
                  ...s,
                  sections: updateSectionAtPath(s.sections, path, patch),
                }))
              }
            />
          </div>

          <label className="flex flex-col gap-1">
            <span className="flex items-center gap-2 text-[0.78rem] font-medium text-stone-600">
              Podnožje (pattern)
              <Stamp
                path="footer.pattern"
                confidence={review.confidence}
                confirmed={confirmed}
                onConfirm={confirm}
              />
            </span>
            <Input
              value={review.spranca.footer.pattern}
              placeholder="npr. {mjesto_upper}, {mjesec} {godina}. godine"
              onChange={(e) =>
                patchSpranca((s) => ({
                  ...s,
                  footer: { ...s.footer, pattern: e.target.value },
                }))
              }
            />
          </label>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              size="sm"
              disabled={busy === "save"}
              onClick={() => void onSave()}
            >
              {busy === "save" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              Spremi šprancu
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setReview(null)}
            >
              Odbaci
            </Button>
            {openCount > 0 && (
              <span className="text-[0.72rem] text-amber-700">
                {openCount} {openCount === 1 ? "polje čeka" : "polja čekaju"} provjeru
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );

  function confirm(path: string) {
    setConfirmed((prev) => new Set([...prev, path]));
  }
}

interface StampProps {
  path: string;
  confidence: Record<string, SprancaFieldConfidence>;
  confirmed: ReadonlySet<string>;
  onConfirm: (path: string) => void;
}

/** Local variant of the extraction ConfidenceStamp: same thresholds and
 * words ("provjeri" / "Potvrdi" / "potvrđeno"), state lives in the panel. */
function Stamp({ path, confidence, confirmed, onConfirm }: StampProps) {
  const meta = confidence[path];
  if (!meta) return null;
  if (confirmed.has(path)) {
    return (
      <span className="inline-flex items-center gap-1 text-[0.7rem] font-medium text-green-700">
        <CheckCircle2 className="h-3 w-3" /> potvrđeno
      </span>
    );
  }
  const isHigh = meta.confidence >= SPRANCA_REVIEW_THRESHOLD;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-[0.7rem] font-medium",
        isHigh ? "text-green-700" : "text-amber-700",
      )}
      title={`Automatski prepoznato — povjerenje ${(meta.confidence * 100).toFixed(0)} %.`}
    >
      {isHigh ? (
        <>
          <CheckCircle2 className="h-3 w-3" /> prepoznato
        </>
      ) : (
        <>
          <AlertCircle className="h-3 w-3" /> provjeri
          <button
            type="button"
            onClick={() => onConfirm(path)}
            className="ml-1 rounded border border-amber-300 bg-amber-100 px-1.5 py-px text-[0.68rem] font-semibold text-amber-800 hover:bg-amber-200"
            title="Vrijednost je točna — makni je iz popisa za provjeru."
          >
            Potvrdi
          </button>
        </>
      )}
    </span>
  );
}

interface SectionTreeProps {
  nodes: SprancaSectionNode[];
  prefix: string;
  confidence: Record<string, SprancaFieldConfidence>;
  confirmed: ReadonlySet<string>;
  onConfirm: (path: string) => void;
  onPatch: (path: string, patch: Partial<SprancaSectionNode>) => void;
}

function SectionTree({
  nodes,
  prefix,
  confidence,
  confirmed,
  onConfirm,
  onPatch,
}: SectionTreeProps) {
  return (
    <ul className={cn("flex flex-col gap-1", prefix !== "" && "ml-4 border-l border-stone-200 pl-3")}>
      {nodes.map((node, i) => {
        const path = `${prefix}${i}`;
        return (
          <li key={path} className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                className="h-8 w-56 text-[0.78rem]"
                value={node.title}
                onChange={(e) => onPatch(path, { title: e.target.value })}
              />
              <Input
                className="h-8 w-16 text-[0.78rem]"
                value={node.oznaka ?? ""}
                placeholder="ozn."
                title="Vidljiva oznaka poglavlja (prazno = bez broja)"
                onChange={(e) => onPatch(path, { oznaka: e.target.value || null })}
              />
              <select
                className="h-8 rounded-md border border-stone-200 bg-white px-1 text-[0.78rem]"
                value={node.klasa}
                title="A = činjenice · B = standardni stručni tekst · C = projektna proza"
                onChange={(e) => onPatch(path, { klasa: e.target.value })}
              >
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
              </select>
              {node.children.length === 0 && (
                <Input
                  className="h-8 w-20 text-[0.78rem]"
                  type="number"
                  min={0}
                  value={node.depth_words}
                  title="Ciljna dubina (broj riječi)"
                  onChange={(e) =>
                    onPatch(path, { depth_words: Number(e.target.value) || 0 })
                  }
                />
              )}
              <Stamp
                path={`sections.${path}`}
                confidence={confidence}
                confirmed={confirmed}
                onConfirm={onConfirm}
              />
            </div>
            {node.children.length === 0 && node.seed_text && (
              <Textarea
                className="min-h-[3rem] text-[0.75rem]"
                value={node.seed_text}
                title="Smjernice tona i sadržaja za ovu sekciju (anonimizirano)"
                onChange={(e) => onPatch(path, { seed_text: e.target.value })}
              />
            )}
            {node.children.length > 0 && (
              <SectionTree
                nodes={node.children}
                prefix={`${path}.`}
                confidence={confidence}
                confirmed={confirmed}
                onConfirm={onConfirm}
                onPatch={onPatch}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
